import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { LoginForm } from '../ui/login-form';
import { withErrorHandling } from '@selfserve/react-hocs';
import { storage, storageKeys, useAuth, useQueryParams, useStorage } from '@selfserve/react-hooks';
import {
  StyledContentContainer,
  StyledTitle,
  CommonLayout, StyledText, StyledLink
} from '@selfserve/react-ui';
import { routesPaths, getRecaptchaToken, responseTypes, links, buttonIds, subscriptionTypes } from '@selfserve/react-utils';

import { useLoginRequest, useLoginByEmailRequest } from '../data';
import { usePackageByProductFamilyRequest } from '@selfserve/react-common-data';

const StyledLoginContainer = styled.div`
  max-width: 584px;
  width: 100%;
  gap: 48px;
  display: flex;
  flex-direction: column;
`;

const StyledLoginTitle = styled(StyledTitle)`
  display: block;
  text-align: center;
`;

const Link = StyledLink.withComponent('a');

const validationSchema = (t: TFunction, passwordlessMode: boolean) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
    ...(!passwordlessMode && {
      password: Yup.string().required(t('common.validations.password-required')),
    }),
  });

export interface FormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

export const Login = withErrorHandling(({ openErrorModal }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setToken } = useAuth();
  const [, setSessionEmail] = useStorage(storage.SESSION, storageKeys.EMAIL);
  const [passwordlessMode, setPasswordlessMode] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const queryParams = useQueryParams();
  const productFamily = queryParams['productFamily'];

  const [packageNameByProductFamily, setPackageNameByProductFamily] = useState<string>('');

  const {
    data: packageData,
    isError: packageRequestError,
  } = usePackageByProductFamilyRequest(productFamily || '');

  useEffect(() => {
    if (packageData?.data && !packageRequestError && String(packageData?.status).startsWith(responseTypes.SUCCESS)) {
      const res = packageData.data;
      const packageName: string = res?.find(pkg => pkg.type === subscriptionTypes.FREE)?.name || '';
      setPackageNameByProductFamily(packageName);
    }
  }, [packageData]);

  const {
    data: loginResponse,
    mutateAsync: handleLoginRequest,
    isLoading: isLoginLoading,
    isSuccess: isLoginSuccess,
  } = useLoginRequest();
  const {
    data: loginByEmailResponse,
    mutateAsync: handleLoginByEmailRequest,
    isLoading: isLoginByEmailLoading,
    variables: loginByEmailVariables,
  } = useLoginByEmailRequest();
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    initialValues: { email: '', password: '', rememberMe: false },
    validationSchema: validationSchema(t, passwordlessMode),
    onSubmit: async ({ email, password }) => {
      if (isLoginLoading || isLoginByEmailLoading || isRecaptchaLoading) return;
      setIsRecaptchaLoading(true);
      try {
        const recaptchaToken = await getRecaptchaToken();
        if (passwordlessMode) {
          handleLoginByEmailRequest({
            email,
            recaptchaToken,
            ...(productFamily && { productFamily }),
          });
        } else {
          handleLoginRequest({
            email,
            password,
            recaptchaToken,
            ...(productFamily && { productFamily }),
          });
        }
      } catch {
        openErrorModal();
      } finally {
        setIsRecaptchaLoading(false);
      }
    },
  });
  const { errors, values, setFieldValue } = formik;

  const handleTogglePasswordlessMode = useCallback(() => {
    setPasswordlessMode(value => !value);
    setFieldValue('password', '');
  }, [setFieldValue]);

  const handleSignupLinkClick = useCallback(
    (link: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (productFamily && packageNameByProductFamily) {
        navigate(routesPaths.GET_STARTED.replace(':desiredPackageId', packageNameByProductFamily));
        return;
      }

      // Fallback to default behavior
      if (link) {
        if (link.includes('http')) {
          window.open(link, '_blank');
        } else {
          navigate(link);
        }
      }
    },
    [navigate, packageNameByProductFamily]
  );

  useEffect(() => {
    const hasError = !!Object.keys(errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors]);

  useEffect(() => {
    if (isLoginSuccess) {
      const { status, data } = loginResponse || {};
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        setToken(data?.access_token as string, values.rememberMe);
        if (data?.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          navigate(routesPaths.MANAGEMENT_OVERVIEW);
        }
      }
      if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
        const { id } = data || {};
        if (id && i18n.exists(`errors.${id}`)) {
          const text = t(`errors.${id}`);
          const title = i18n.exists(`error-titles.${id}`)
            ? t(`error-titles.${id}`)
            : '';
          openErrorModal({ text, title });
        } else {
          openErrorModal();
        }
      }
    }
  }, [loginResponse, navigate]);

  useEffect(() => {
    const { status } = loginByEmailResponse || {};
    if (String(status).startsWith(responseTypes.SUCCESS)) {
      setSessionEmail(loginByEmailVariables?.email);
      navigate(
        productFamily
          ? `${routesPaths.CHECK_YOUR_INBOX}?productFamily=${productFamily}`
          : routesPaths.CHECK_YOUR_INBOX
      );
    }
    if (String(status).startsWith(responseTypes.CLIENT_ERROR)) {
      openErrorModal();
    }
  }, [loginByEmailResponse]);

  return (
    <>
      <Helmet>
        <title>{passwordlessMode ? t('common.login-without-password') : t('titles.login')}</title>
      </Helmet>
      <CommonLayout showSideImage>
        <StyledContentContainer centered>
          <StyledLoginContainer>
            {/* HEADER */}
            <StyledLoginTitle className="signHeaderText">
              {passwordlessMode ? t('common.login-without-password') : t('common.login')}
            </StyledLoginTitle>
            {/* FORM */}
            <LoginForm
              formik={formik}
              isLoading={isLoginLoading || isLoginByEmailLoading || isRecaptchaLoading}
              passwordlessMode={passwordlessMode}
              handleTogglePasswordlessMode={handleTogglePasswordlessMode}
              openErrorModal={openErrorModal}
            />
            {/* SIGNUP LINK*/}
            <StyledText style={{
              display: 'flex',
              gap: '4px',
              justifyContent: 'center',
            }}>
              <span>{t('common.dont-have-account')}</span>{' '}
              <Link
                to=""
                href={links.SIGN_UP}
                id={buttonIds.signUp}
                onClick={handleSignupLinkClick(links.PRICING)}
              >
                {t('common.sign-up')}
              </Link>
            </StyledText>
          </StyledLoginContainer>
        </StyledContentContainer>
      </CommonLayout>
    </>
  );
});
